<template>
    <validation-observer ref="refForm">

        <b-form @submit.prevent="onSubmit">

            <b-row>
                <b-col md="12">
                    <b-card footer-tag="footer">

                        <b-row>

                            <!-- URL -->
                            <b-col cols="12">
                                <b-form-group label-for="url">
                                    <template #label>
                                        <strong>ENLACE DE LA NOTICIA</strong>
                                    </template>
                                    <validation-provider
                                        #default="{ errors }"
                                        rules="required"
                                        name="enlace"
                                    >
                                        <b-input-group>
                                            <b-form-input
                                                id="url"
                                                v-model="formData.url"
                                                placeholder="https://..."
                                                :state="errors.length > 0 ? false : null"
                                                trim
                                            />
                                            <b-input-group-append>
                                                <b-button
                                                    variant="outline-primary"
                                                    :disabled="fetching || !formData.url"
                                                    @click="fetchPreview"
                                                >
                                                    <b-spinner small v-if="fetching" />
                                                    <span v-else>Volver a traer datos</span>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                    <small class="text-muted">Al volver a traer datos se sobrescriben los campos con lo que publique el medio.</small>
                                </b-form-group>
                            </b-col>

                            <!-- TITLE -->
                            <b-col cols="12" md="8">
                                <b-form-group label-for="title">
                                    <template #label>
                                        <strong>TÍTULO</strong>
                                    </template>
                                    <validation-provider
                                        #default="{ errors }"
                                        rules="required"
                                        name="título"
                                    >
                                        <b-form-input
                                            id="title"
                                            v-model="formData.title"
                                            maxlength="250"
                                            :state="errors.length > 0 ? false : null"
                                            trim
                                        />
                                        <small class="textarea-counter-value float-right">
                                            <strong class="char-count">{{ formData.title ? formData.title.length : 0 }} / 250</strong>
                                        </small>
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>
                            </b-col>

                            <!-- SOURCE -->
                            <b-col cols="12" md="4">
                                <b-form-group label-for="source">
                                    <template #label>
                                        <strong>MEDIO</strong>
                                    </template>
                                    <b-form-input
                                        id="source"
                                        v-model="formData.source"
                                        maxlength="120"
                                        trim
                                    />
                                    <small class="textarea-counter-value float-right">
                                        <strong class="char-count">{{ formData.source ? formData.source.length : 0 }} / 120</strong>
                                    </small>
                                </b-form-group>
                            </b-col>

                            <!-- DESCRIPTION -->
                            <b-col cols="12">
                                <b-form-group label-for="description">
                                    <template #label>
                                        <strong>DESCRIPCIÓN</strong>
                                    </template>
                                    <b-form-textarea
                                        id="description"
                                        v-model="formData.description"
                                        rows="3"
                                        maxlength="400"
                                    />
                                    <small class="textarea-counter-value float-right">
                                        <strong class="char-count">{{ formData.description ? formData.description.length : 0 }} / 400</strong>
                                    </small>
                                </b-form-group>
                            </b-col>

                            <!-- IMAGE URL -->
                            <b-col cols="12" md="8">
                                <b-form-group label-for="image_url">
                                    <template #label>
                                        <strong>IMAGEN (URL)</strong>
                                    </template>
                                    <b-form-input
                                        id="image_url"
                                        v-model="formData.image_url"
                                        placeholder="https://... (opcional)"
                                        trim
                                    />
                                </b-form-group>
                            </b-col>

                            <!-- PREVIEW -->
                            <b-col cols="12" v-if="formData.title">
                                <p class="mb-50"><strong>VISTA PREVIA</strong></p>
                                <b-card class="border mb-2" no-body>
                                    <div class="d-flex">
                                        <b-img
                                            v-if="formData.image_url"
                                            :src="formData.image_url"
                                            width="140"
                                            height="100"
                                            style="object-fit: cover; border-radius: .357rem 0 0 .357rem;"
                                            alt="portada"
                                        />
                                        <div class="p-1">
                                            <small class="text-muted">{{ formData.source }}</small>
                                            <h6 class="mb-25">{{ formData.title }}</h6>
                                            <small>{{ formData.description }}</small>
                                        </div>
                                    </div>
                                </b-card>
                            </b-col>

                            <!-- PUBLISHED AT -->
                            <b-col cols="12" md="4">
                                <b-form-group label-for="published_at">
                                    <template #label>
                                        <strong>FECHA DE PUBLICACIÓN</strong>
                                    </template>
                                    <flat-pickr
                                        id="published_at"
                                        v-model="formData.published_at"
                                        class="form-control"
                                        :config="dateConfig"
                                        placeholder="YYYY-MM-DD"
                                    />
                                    <small class="text-muted">Es la fecha en que el medio publicó la nota, no la de hoy. Si queda vacía, la web no muestra fecha.</small>
                                </b-form-group>
                            </b-col>

                            <!-- ORDER -->
                            <b-col cols="12" md="4">
                                <b-form-group label-for="display_order">
                                    <template #label>
                                        <strong>ORDEN EN LA WEB</strong>
                                    </template>
                                    <validation-provider
                                        #default="{ errors }"
                                        rules="integer"
                                        name="orden"
                                    >
                                        <b-form-input
                                            id="display_order"
                                            v-model="formData.display_order"
                                            type="number"
                                            min="0"
                                            placeholder="Vacío = al final ..."
                                            :state="errors.length > 0 ? false : null"
                                            trim
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                    <small class="text-muted">Define la posición en la que aparece en la página pública. El menor número va primero. Si el número ya está en uso, los demás se recorren automáticamente. Si lo dejas vacío, se muestra después de los que sí tienen orden, alfabéticamente por título.</small>
                                </b-form-group>
                            </b-col>

                        </b-row>

                        <template #footer>
                            <b-button-loading
                                text="ACTUALIZAR"
                                type="submit"
                                :processing="processing">
                            </b-button-loading>

                            <b-button
                                variant="outline-secondary"
                                class="ml-1"
                                :to="{ name: 'news' }"
                            >
                                CANCELAR
                            </b-button>
                        </template>

                    </b-card>
                </b-col>
            </b-row>

        </b-form>

    </validation-observer>
</template>

<script>

    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
    import BButtonLoading from '@core/components/b-button-loading/BButtonLoading.vue';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { useToast } from 'vue-toastification/composition';
    import { ref } from '@vue/composition-api';
    import { Spanish } from 'flatpickr/dist/l10n/es';
    import flatPickr from 'vue-flatpickr-component';
    import Ripple from 'vue-ripple-directive';
    import { required } from '@validations';
    import router from '@/router';
    import store from '@/store';

    export default {
        components: {
            ValidationProvider,
            ValidationObserver,
            BButtonLoading,
            flatPickr,
            required
        },
        directives: {
            Ripple
        },
        props: {
            newsData: {
                type: Object,
                required: true
            }
        },
        setup(props) {

            const toast = useToast();

            const processing = ref(false);
            const fetching = ref(false);
            const refForm = ref(null);

            // Sin hora: de la fecha de publicación solo interesa el día.
            const dateConfig = { enableTime: false, locale: Spanish };

            const formData = ref({
                url: props.newsData.url,
                title: props.newsData.title,
                description: props.newsData.description,
                image_url: props.newsData.image_url,
                source: props.newsData.source,
                // Llega en ISO desde el API; flatpickr espera solo el día.
                published_at: props.newsData.published_at ? String(props.newsData.published_at).slice(0, 10) : null,
                display_order: props.newsData.display_order
            });

            const showToast = (title, icon, variant) => {
                toast({
                    component: ToastificationContent,
                    props: { title, icon, variant }
                });
            };

            const fetchPreview = () => {

                if (!formData.value.url) return;

                fetching.value = true;

                store.dispatch('app-news/fetchLinkPreview', { url: formData.value.url })
                    .then(response => {

                        const { success, preview, message } = response.data;

                        if (!success) {
                            showToast(message, 'AlertTriangleIcon', 'warning');
                            fetching.value = false;
                            return;
                        }

                        // En edición sí se sobrescribe: el usuario pidió refrescar.
                        if (preview.title) formData.value.title = preview.title;
                        if (preview.description) formData.value.description = preview.description;
                        if (preview.image) formData.value.image_url = preview.image;
                        if (preview.source) formData.value.source = preview.source;

                        // Llega en ISO; flatpickr y el backend esperan el día.
                        if (preview.publishedAt) formData.value.published_at = preview.publishedAt.slice(0, 10);

                        showToast('Datos del enlace actualizados.', 'CheckIcon', 'success');
                        fetching.value = false;

                    })
                    .catch(err => {
                        const message = err.response && err.response.data.message ? err.response.data.message : 'No se pudo leer el enlace.';
                        showToast(message, 'AlertTriangleIcon', 'danger');
                        fetching.value = false;
                    });

            };

            const onSubmit = async () => {

                const isValid = await refForm.value.validate();

                if (!isValid) {
                    showToast('Complete los campos requeridos.', 'AlertTriangleIcon', 'warning');
                    return;
                }

                processing.value = true;

                store.dispatch('app-news/updateNews', { id: router.currentRoute.params.id, payload: { ...formData.value } })
                    .then(response => {
                        showToast(response.data.message, 'CheckIcon', 'success');
                        processing.value = false;
                    })
                    .catch(err => {
                        const message = err.response && err.response.data.message ? err.response.data.message : 'Error al actualizar la noticia.';
                        showToast(message, 'AlertTriangleIcon', 'danger');
                        processing.value = false;
                    });

            };

            return {
                processing,
                fetching,
                formData,
                dateConfig,
                refForm,

                fetchPreview,
                onSubmit
            }
        }
    }

</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
